import React, {useEffect, useMemo, useRef, useState, useCallback} from 'react'
import PropTypes from 'prop-types'
import {Box, useTheme, Heading, IconButton, Text, Link as ChakraLink} from '@chakra-ui/react'
import {Link as RouterLink} from 'react-router-dom'
import useCarouselBleed from '../../../../hooks/use-carousel-bleed'
import {ChevronLeftIcon, ChevronRightIcon} from '@chakra-ui/icons'
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../../product-tile'
import {generateStyles, getHideStyles} from '../../utils/utils'
import {useGetLinkData} from '../../../../contexts/category-url-context'
import {useInView} from '../../../../hooks/use-in-view'
import {useHeaderContext} from '../../../../contexts/header-context'
import {sendViewItemListEvent} from '../../../../utils/gtm-utils'

/* Constants */
const GAP_SIZE = 8 // Gap between carousel items in pixels

/**
 * CTATileSlide - Renders the CTA tile as a carousel slide
 * Displays title, link text with configurable background and positioning
 */
const CTATileSlide = ({content}) => {
    const getLinkData = useGetLinkData()

    // Get link data from CMS
    const linkData = useMemo(() => {
        return getLinkData(content?.Link?.Expanded)
    }, [content?.Link?.Expanded])

    // Extract CMS properties with fallbacks
    const backgroundColor = content?.BackgroundColor || content?.BackgroundColorHex || '#F0EFEB'
    const title = content?.Title || ''
    const linkText = content?.CtaLinkText || content?.LinkText || 'SHOP ALL'
    const position = content?.Position || content?.Alignment || 'center/center'

    // Parse position (format: "vertical/horizontal" e.g., "center/center", "top/left")
    const [verticalAlign, horizontalAlign] = position.split('/')
    const getFlexAlign = (align) => {
        switch (align?.toLowerCase()) {
            case 'top':
            case 'left':
                return 'flex-start'
            case 'bottom':
            case 'right':
                return 'flex-end'
            default:
                return 'center'
        }
    }

    const isExternalUrl = linkData?.isExternalUrl || false
    const linkComponent = isExternalUrl ? ChakraLink : RouterLink

    const content_inner = (
        <>
            {/* Title */}
            {title && (
                <Text
                    fontSize={{base: '20px', lg: '48px'}}
                    fontWeight="bold"
                    textTransform="uppercase"
                    textAlign={horizontalAlign || 'center'}
                    lineHeight={{base: '20px', lg: '48px'}}
                    mb={4}
                >
                    {title}
                </Text>
            )}

            {/* Link Text */}
            <Text
                fontSize="sm"
                fontWeight="bold"
                textTransform="uppercase"
                textDecoration="underline"
            >
                {linkText}
            </Text>
        </>
    )

    return (
        <Box
            as={linkData?.generatedURL ? linkComponent : 'div'}
            {...(linkData?.generatedURL &&
                (isExternalUrl ? {href: linkData?.generatedURL} : {to: linkData?.generatedURL}))}
            {...(linkData?.generatedURL &&
                !linkData?.stayOnSamePage && {
                    target: linkData?.target,
                    rel: linkData?.rel
                })}
            width="100%"
            aspectRatio="19/28"
            backgroundColor={backgroundColor}
            display="flex"
            flexDirection="column"
            alignItems={getFlexAlign(horizontalAlign)}
            justifyContent={getFlexAlign(verticalAlign)}
            padding={6}
            overflow="hidden"
            _hover={{
                textDecoration: 'none'
            }}
        >
            {content_inner}
        </Box>
    )
}

CTATileSlide.propTypes = {
    content: PropTypes.object
}

/**
 * BaseProductCarousel - Base carousel component for rendering products.
 * Pure UI rendering - receives products array and renders carousel.
 * Used by RecommendationsCarousel (VickyAI) and ProductCarouselBlock (CMS IDs).
 */
const BaseProductCarousel = ({
    Title,
    TitleStyle,
    ProductTitleStyle,
    ProductPriceStyle,
    products = [],
    isLoading = false,
    TileNumber,
    TabletTileNumber,
    DesktopTileNumber,
    TileSlideNumber,
    slidesToShowMobile = 2.3,
    slidesToShowTablet = 3.5,
    slidesToShowDesktop = 4.5,
    carouselID,
    showAsGrid,
    onSkusUpdate,
    onProductClick,
    HideOnDevices,
    CarouselCTATile,
    hydrationCount = 0 // From withViewportHydration HOC - triggers re-measurement
}) => {
    // Calculate effective slides per view with CMS overrides
    const effectiveSlidesToShowMobile = TileNumber ? parseFloat(TileNumber) : slidesToShowMobile
    const effectiveSlidesToShowTablet = TabletTileNumber
        ? parseFloat(TabletTileNumber)
        : slidesToShowTablet
    const effectiveSlidesToShowDesktop = DesktopTileNumber
        ? parseFloat(DesktopTileNumber)
        : slidesToShowDesktop

    // Grid modes: true (desktop only), 'desktop-only' (same), 'always' (all breakpoints including mobile)
    const isAlwaysGrid = showAsGrid === 'always'
    const isDesktopOnlyGrid = showAsGrid === 'desktop-only'
    const shouldUseGrid = showAsGrid === true || isDesktopOnlyGrid || isAlwaysGrid

    const theme = useTheme()
    const mdBreakpoint = parseFloat(theme.breakpoints.md) * 16
    const lgBreakpoint = parseFloat(theme.breakpoints.lg) * 16
    const numberOfRows = shouldUseGrid
        ? Math.ceil(products?.length / effectiveSlidesToShowDesktop)
        : 1

    // Get the CTA tile content from CMS
    const ctaTileContent = CarouselCTATile?.Expanded

    // Ref for CSS scroll container
    const scrollContainerRef = useRef(null)

    // State for arrow visibility based on scroll position
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(true)

    // Carousel bleed: determine if carousel should extend beyond page margins
    const {
        containerRef,
        shouldBleedMobile,
        shouldBleedTablet,
        shouldBleedDesktop,
        mobileOffset,
        tabletOffset,
        desktopOffset,
        bleedMargin,
        isScrollableDesktop,
        isAlreadyFullViewport
    } = useCarouselBleed({
        // Include CTA tile in item count if present
        itemCount: (products?.length || 0) + (ctaTileContent ? 1 : 0),
        slidesToShowMobile: effectiveSlidesToShowMobile,
        slidesToShowTablet: effectiveSlidesToShowTablet,
        slidesToShowDesktop: effectiveSlidesToShowDesktop,
        remeasureTrigger: hydrationCount // Re-measure when hydration occurs
    })

    const {headerHeight} = useHeaderContext()
    const {ref: headerRef, isInView} = useInView({
        rootMargin: `${headerHeight}px`
    })

    // Calculate dynamic image widths based on slides to show at each breakpoint
    const imageWidths = [
        `${Math.ceil(100 / effectiveSlidesToShowMobile)}vw`, // base (mobile)
        `${Math.ceil(100 / effectiveSlidesToShowMobile)}vw`, // sm (still mobile)
        `${Math.ceil(100 / effectiveSlidesToShowTablet)}vw`, // md (tablet)
        `${Math.ceil(100 / effectiveSlidesToShowDesktop)}vw` // lg+ (desktop)
    ]

    // Update arrow visibility based on scroll position
    const updateScrollState = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container) return

        const {scrollLeft, scrollWidth, clientWidth} = container
        setCanScrollLeft(scrollLeft > 1)
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
    }, [])

    // Calculate scroll amount for one tile (tile width + gap)
    const getScrollAmount = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container) return 300 // Fallback

        const containerWidth = container.clientWidth
        const slidesToShow = effectiveSlidesToShowDesktop
        const gaps = (Math.floor(slidesToShow) - 1) * GAP_SIZE

        // Calculate tile width matching the CSS calc
        // When bleeding: (100% + offset - gaps) / slidesToShow
        // When not bleeding: (100% - gaps) / slidesToShow
        const tileWidth = shouldBleedDesktop
            ? (containerWidth + desktopOffset - gaps) / slidesToShow
            : (containerWidth - gaps) / slidesToShow

        return tileWidth + GAP_SIZE
    }, [effectiveSlidesToShowDesktop, shouldBleedDesktop, desktopOffset])

    // Handle arrow clicks for desktop navigation
    const handleScrollLeft = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container) return

        const scrollAmount = getScrollAmount()
        const {scrollLeft} = container

        // If near the start (within one tile), scroll to beginning including offset
        if (scrollLeft < scrollAmount + desktopOffset) {
            container.scrollTo({left: 0, behavior: 'smooth'})
        } else {
            container.scrollBy({left: -scrollAmount, behavior: 'smooth'})
        }
    }, [getScrollAmount, desktopOffset])

    const handleScrollRight = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container) return

        const scrollAmount = getScrollAmount()
        const {scrollLeft, scrollWidth, clientWidth} = container
        const maxScroll = scrollWidth - clientWidth

        // If near the end (within one tile), scroll to the end including offset
        if (scrollLeft > maxScroll - scrollAmount - desktopOffset) {
            container.scrollTo({left: maxScroll, behavior: 'smooth'})
        } else {
            container.scrollBy({left: scrollAmount, behavior: 'smooth'})
        }
    }, [getScrollAmount, desktopOffset])

    // Set up scroll listener for arrow visibility (desktop only)
    useEffect(() => {
        const container = scrollContainerRef.current
        if (!container || typeof window === 'undefined' || !isScrollableDesktop) return

        // Initial check
        updateScrollState()

        // Listen for scroll events
        container.addEventListener('scroll', updateScrollState, {passive: true})
        window.addEventListener('resize', updateScrollState, {passive: true})

        return () => {
            container.removeEventListener('scroll', updateScrollState)
            window.removeEventListener('resize', updateScrollState)
        }
    }, [updateScrollState, isScrollableDesktop, products])

    // Use useEffect to call onSkusUpdate when products change (including loading states)
    useEffect(() => {
        if (onSkusUpdate) {
            if (isLoading) {
                // During loading, show the carousel with skeleton content
                onSkusUpdate(4) // Show 4 skeleton items
            } else if (products && products.length > 0) {
                // When products are loaded, show actual count
                const skuCount = products.length / numberOfRows
                onSkusUpdate(skuCount)
            } else {
                // When no products, hide the carousel
                onSkusUpdate(0)
            }
        }
    }, [products, onSkusUpdate, numberOfRows, isLoading, Title])

    useEffect(() => {
        if (isInView && products && products.length) {
            const carouselData = {
                id: 'recommendations',
                name: Title
            }
            sendViewItemListEvent(carouselData, products, true)
        }
    }, [isInView])

    const hideStyles = getHideStyles(HideOnDevices)

    if (hideStyles.doNotLoadComponent) return

    // Don't render if no products and not loading
    if (!isLoading && (!products || products.length === 0)) {
        return null
    }

    // Number of skeleton items to show (based on desktop slides)
    const skeletonCount = Math.ceil(effectiveSlidesToShowDesktop)

    // Calculate if we need navigation arrows (only show if carousel is scrollable)
    const shouldShowArrows = isScrollableDesktop

    // CSS-only scrollable carousel
    const renderCSSCarousel = () => (
        <Box
            ref={scrollContainerRef}
            display={isAlwaysGrid ? 'grid' : 'flex'}
            gridTemplateColumns={
                isAlwaysGrid
                    ? `repeat(${Math.floor(effectiveSlidesToShowDesktop)}, 1fr)`
                    : undefined
            }
            gap={`${GAP_SIZE}px`}
            overflowX={isAlwaysGrid ? 'visible' : 'auto'}
            mx={isAlwaysGrid ? 0 : bleedMargin}
            sx={{
                // For always-grid mode (mini cart), ensure children fit within grid cells
                ...(isAlwaysGrid && {
                    '& > *': {
                        minWidth: 0,
                        width: '100%'
                    }
                }),
                // For non-always-grid modes
                ...(!isAlwaysGrid && {
                    // Hide scrollbar across browsers
                    scrollbarWidth: 'none', // Firefox
                    msOverflowStyle: 'none', // IE/Edge
                    '&::-webkit-scrollbar': {
                        display: 'none' // Chrome/Safari/Opera
                    },
                    // Smooth scrolling on touch devices
                    WebkitOverflowScrolling: 'touch',
                    // Consistent item sizing - account for padding when bleeding
                    '& > *': {
                        flexShrink: 0,
                        width: shouldBleedMobile
                            ? `calc((100% + ${mobileOffset}px - ${
                                  (Math.floor(effectiveSlidesToShowMobile) - 1) * GAP_SIZE
                              }px) / ${effectiveSlidesToShowMobile})`
                            : `calc((100% - ${
                                  (Math.floor(effectiveSlidesToShowMobile) - 1) * GAP_SIZE
                              }px) / ${effectiveSlidesToShowMobile})`
                    },
                    [`@media (min-width: ${mdBreakpoint}px)`]: {
                        '& > *': {
                            width: shouldBleedTablet
                                ? `calc((100% + ${tabletOffset}px - ${
                                      (Math.floor(effectiveSlidesToShowTablet) - 1) * GAP_SIZE
                                  }px) / ${effectiveSlidesToShowTablet})`
                                : `calc((100% - ${
                                      (Math.floor(effectiveSlidesToShowTablet) - 1) * GAP_SIZE
                                  }px) / ${effectiveSlidesToShowTablet})`
                        }
                    },
                    [`@media (min-width: ${lgBreakpoint}px)`]: {
                        // For grid mode on desktop, use CSS grid instead of flex
                        ...(shouldUseGrid && {
                            display: 'grid',
                            gridTemplateColumns: `repeat(${Math.floor(
                                effectiveSlidesToShowDesktop
                            )}, 1fr)`,
                            gridTemplateRows: `repeat(${numberOfRows}, auto)`,
                            overflowX: 'visible',
                            '& > *': {
                                width: 'auto'
                            }
                        }),
                        ...(!shouldUseGrid && {
                            '& > *': {
                                width: shouldBleedDesktop
                                    ? `calc((100% + ${desktopOffset}px - ${
                                          (Math.floor(effectiveSlidesToShowDesktop) - 1) * GAP_SIZE
                                      }px) / ${effectiveSlidesToShowDesktop})`
                                    : `calc((100% - ${
                                          (Math.floor(effectiveSlidesToShowDesktop) - 1) * GAP_SIZE
                                      }px) / ${effectiveSlidesToShowDesktop})`
                            }
                        })
                    }
                })
            }}
            // Add padding for bleed effect (only for non-grid mode)
            pl={{
                base: !isAlwaysGrid && shouldBleedMobile ? `${mobileOffset}px` : 0,
                md: !isAlwaysGrid && shouldBleedTablet ? `${tabletOffset}px` : 0,
                lg: !isAlwaysGrid && shouldBleedDesktop && !shouldUseGrid ? `${desktopOffset}px` : 0
            }}
            pr={{
                base: !isAlwaysGrid && shouldBleedMobile ? `${mobileOffset}px` : 0,
                md: !isAlwaysGrid && shouldBleedTablet ? `${tabletOffset}px` : 0,
                lg: !isAlwaysGrid && shouldBleedDesktop && !shouldUseGrid ? `${desktopOffset}px` : 0
            }}
        >
            {isLoading
                ? // Render skeleton items inside the carousel (same as PLP)
                  Array.from({length: skeletonCount}).map((_, index) => (
                      <Box key={`skeleton-${index}`}>
                          <ProductTileSkeleton />
                      </Box>
                  ))
                : // Render actual products
                  products.map((product, index) => (
                      <Box key={product.id || index}>
                          <ProductTile
                              data-testid="vicky-product-carousel-item"
                              product={product}
                              disableImageScroll={true}
                              enableFavourite={true}
                              showQV={false}
                              productTitleStyle={ProductTitleStyle}
                              productPriceStyle={ProductPriceStyle}
                              dynamicImageProps={{
                                  widths: imageWidths
                              }}
                              onClick={onProductClick}
                          />
                      </Box>
                  ))}
            {/* CTA Tile - renders as the last item when configured */}
            {!isLoading && ctaTileContent && (
                <Box key="carousel-cta-tile">
                    <CTATileSlide content={ctaTileContent} />
                </Box>
            )}
        </Box>
    )

    return (
        <Box ref={containerRef} position="relative" {...hideStyles}>
            {/* Carousel Title */}
            {Title && (
                <Heading
                    ref={headerRef}
                    mb={4}
                    px={{
                        base: isAlreadyFullViewport ? 4 : 0,
                        lg: isAlreadyFullViewport ? 6 : 0
                    }}
                    {...generateStyles(TitleStyle, {
                        fontSize: {base: '14px', lg: '20px'},
                        fontWeight: 'bold',
                        color: 'inherit',
                        textTransform: 'uppercase'
                    })}
                >
                    {Title}
                </Heading>
            )}

            {/* Navigation Arrows - only show if needed and not in grid mode */}
            {shouldShowArrows && !shouldUseGrid && (
                <>
                    <IconButton
                        aria-label="Previous slide"
                        icon={<ChevronLeftIcon role="presentation" />}
                        className={`element-prev-${carouselID}`}
                        position="absolute"
                        left={shouldBleedDesktop && !isAlreadyFullViewport ? '-24px' : 0}
                        top="50%"
                        transform="translateY(-50%)"
                        zIndex={10}
                        bg="#FFFFFF80"
                        backdropFilter="blur(10px)"
                        color="black"
                        boxShadow="sm"
                        _hover={{
                            bg: 'gray.50'
                        }}
                        _active={{
                            bg: 'gray.100'
                        }}
                        size="sm"
                        width="40px"
                        height="40px"
                        display={{base: 'none', lg: canScrollLeft ? 'flex' : 'none'}}
                        onClick={handleScrollLeft}
                    />

                    <IconButton
                        aria-label="Next slide"
                        icon={<ChevronRightIcon role="presentation" />}
                        className={`element-next-${carouselID}`}
                        position="absolute"
                        right={shouldBleedDesktop && !isAlreadyFullViewport ? '-24px' : 0}
                        top="50%"
                        transform="translateY(-50%)"
                        zIndex={10}
                        bg="#FFFFFF80"
                        backdropFilter="blur(10px)"
                        color="black"
                        boxShadow="sm"
                        _hover={{
                            bg: 'gray.50'
                        }}
                        _active={{
                            bg: 'gray.100'
                        }}
                        size="sm"
                        width="40px"
                        height="40px"
                        display={{base: 'none', lg: canScrollRight ? 'flex' : 'none'}}
                        onClick={handleScrollRight}
                    />
                </>
            )}

            {/* CSS Scroll Carousel */}
            {renderCSSCarousel()}
        </Box>
    )
}

BaseProductCarousel.propTypes = {
    Title: PropTypes.string,
    TitleStyle: PropTypes.object,
    ProductTitleStyle: PropTypes.object,
    ProductPriceStyle: PropTypes.object,
    products: PropTypes.arrayOf(PropTypes.object),
    isLoading: PropTypes.bool,
    TileNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    TabletTileNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    DesktopTileNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    TileSlideNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    slidesToShowMobile: PropTypes.number,
    slidesToShowTablet: PropTypes.number,
    slidesToShowDesktop: PropTypes.number,
    carouselID: PropTypes.string,
    showAsGrid: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    onSkusUpdate: PropTypes.func,
    onProductClick: PropTypes.func,
    HideOnDevices: PropTypes.string,
    CarouselCTATile: PropTypes.shape({
        Expanded: PropTypes.object
    }),
    hydrationCount: PropTypes.number
}

export default BaseProductCarousel
